import { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, ExternalLink } from "lucide-react";

import { AlertNote } from "../AlertNote";
import { Badge } from "../Badge";
import { Button } from "../Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../Collapsible";
import { UnifiedModal } from "../UnifiedModal";
import { BADGE_TONES } from "../../theme/badgeTones";
import { PromptField } from "./PromptField";

// Above UnifiedModal (100000): it also opens from the "Regenerar con IA" dialogs.
const MODAL_Z_INDEX = 100001;
const CONFIRM_Z_INDEX = 100002;

const PROMPT_SOURCES = ["agency", "generic", "default"];

const compactBadgeSx = { py: "0.25rem", fontSize: "0.6875rem", minWidth: 0 };

function PromptItem({ prompt, canEdit, onSave, onDirtyChange }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { Icon, PlatformIcon } = prompt;
  const color = prompt.color || theme.palette.primary.main;
  const contentId = `agency-prompt-${prompt.key}`;
  const isGeneric = prompt.source === "generic";

  const handleDirtyChange = useCallback(
    (value) => {
      setDirty(value);
      onDirtyChange(prompt.key, value);
    },
    [onDirtyChange, prompt.key]
  );

  return (
    <Collapsible
      sx={{
        border: `1px solid ${dirty ? BADGE_TONES.pending.color : theme.palette.divider}`,
        borderRadius: "12px",
        backgroundColor: theme.palette.background.paper,
        overflow: "hidden",
        flexShrink: 0,
        transition: "border-color .2s",
      }}
    >
      <CollapsibleTrigger onClick={() => setOpen((prev) => !prev)} aria-expanded={open} aria-controls={contentId}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 1.75, py: 1.25 }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: "8px",
              flexShrink: 0,
              backgroundColor: `${color}1A`,
              color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {PlatformIcon ? <PlatformIcon sx={{ fontSize: 17, color }} /> : <Icon size={16} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: theme.palette.text.primary, lineHeight: 1.3 }}>
              {prompt.label}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, lineHeight: 1.3 }} noWrap>
              {prompt.description}
            </Typography>
          </Box>
          {dirty && (
            <Badge color="pending" sx={compactBadgeSx}>
              Sin guardar
            </Badge>
          )}
          <ChevronDown
            size={16}
            color={theme.palette.text.secondary}
            style={{ flexShrink: 0, transition: "transform .2s", transform: open ? "rotate(180deg)" : "none" }}
          />
        </Box>
      </CollapsibleTrigger>
      <CollapsibleContent open={open} id={contentId}>
        <Box sx={{ px: 1.75, pt: 1.25, pb: 0.75, borderTop: `1px solid ${theme.palette.divider}` }}>
          <PromptField
            id={`${contentId}-field`}
            ariaLabel={`Prompt de ${prompt.label}`}
            value={canEdit ? prompt.ownValue : prompt.value}
            readOnly={!canEdit}
            onSave={(text) => onSave(prompt.settingsKey, text)}
            onDirtyChange={handleDirtyChange}
            placeholder={
              isGeneric ? `Escribe las reglas para ${prompt.label}...` : undefined
            }
            autoExpand
            rows={4}
            maxRows={14}
          />
        </Box>
      </CollapsibleContent>
    </Collapsible>
  );
}

PromptItem.propTypes = {
  prompt: PropTypes.shape({
    key: PropTypes.string.isRequired,
    settingsKey: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    description: PropTypes.string,
    value: PropTypes.string.isRequired,
    ownValue: PropTypes.string.isRequired,
    source: PropTypes.oneOf(PROMPT_SOURCES).isRequired,
    color: PropTypes.string,
    Icon: PropTypes.elementType,
    PlatformIcon: PropTypes.elementType,
  }).isRequired,
  canEdit: PropTypes.bool,
  onSave: PropTypes.func,
  onDirtyChange: PropTypes.func.isRequired,
};

/**
 * Agency prompts that a generation step uses. Editable in place when `canEdit`
 * (each prompt saves on its own); read-only otherwise.
 */
export function AgencyPromptsModal({
  open,
  onClose,
  prompts,
  isLoading,
  isError,
  onRetry,
  canEdit = false,
  onSave,
  onOpenSettings,
  description,
}) {
  const theme = useTheme();
  const [dirtyKeys, setDirtyKeys] = useState({});
  // What to do once the user confirms discarding unsaved edits.
  const [pendingAction, setPendingAction] = useState(null);
  const hasDirty = Object.values(dirtyKeys).some(Boolean);

  const handleDirtyChange = useCallback((key, value) => {
    setDirtyKeys((prev) => (Boolean(prev[key]) === value ? prev : { ...prev, [key]: value }));
  }, []);

  const runGuarded = (action) => {
    if (hasDirty) {
      setPendingAction(() => action);
      return;
    }
    action();
  };

  const confirmDiscard = () => {
    const action = pendingAction;
    setPendingAction(null);
    setDirtyKeys({});
    action?.();
  };

  let body;
  if (isLoading) {
    body = (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 6, color: theme.palette.text.secondary }}>
        <CircularProgress size={24} />
        <Typography variant="body2">Cargando prompts...</Typography>
      </Box>
    );
  } else if (isError) {
    body = (
      <AlertNote
        variant="warning"
        action={
          onRetry && (
            <Button variant="outline" size="xs" onClick={onRetry}>
              Reintentar
            </Button>
          )
        }
      >
        No pudimos cargar los prompts de tu agencia.
      </AlertNote>
    );
  } else {
    body = prompts.map((prompt) => (
      <PromptItem
        key={prompt.key}
        prompt={prompt}
        canEdit={canEdit}
        onSave={onSave}
        onDirtyChange={handleDirtyChange}
      />
    ));
  }

  return (
    <>
      <UnifiedModal
        open={open}
        onClose={() => runGuarded(onClose)}
        title="Prompts de la agencia"
        maxWidth="md"
        disableExtremeZIndex
        sx={{ zIndex: MODAL_Z_INDEX }}
        aria-label="Prompts de la agencia"
        contentSx={{ display: "flex", flexDirection: "column", gap: 1.25, p: { xs: 2, sm: 3 } }}
      >
        <AlertNote variant="info" sx={{ mb: 0.75 }}>
          {description}
        </AlertNote>
        {body}
        {onOpenSettings && !isLoading && !isError && prompts.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
            <Button variant="link" size="xs" onClick={() => runGuarded(onOpenSettings)} sx={{ color: theme.palette.text.secondary }}>
              <ExternalLink size={13} />
              Ver todos los prompts en Ajustes
            </Button>
          </Box>
        )}
      </UnifiedModal>

      <UnifiedModal
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        title="Cambios sin guardar"
        maxWidth="xs"
        disableExtremeZIndex
        sx={{ zIndex: CONFIRM_Z_INDEX }}
      >
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
          Has editado algún prompt y no has guardado los cambios. Si sales ahora, se perderán.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button variant="outline" onClick={() => setPendingAction(null)}>
            Seguir editando
          </Button>
          <Button variant="destructive" onClick={confirmDiscard}>
            Descartar cambios
          </Button>
        </Box>
      </UnifiedModal>
    </>
  );
}

AgencyPromptsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prompts: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onRetry: PropTypes.func,
  canEdit: PropTypes.bool,
  onSave: PropTypes.func,
  onOpenSettings: PropTypes.func,
  description: PropTypes.string.isRequired,
};
